import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import PageNavigation from "@/components/PageNavigation";
import SEOHead from "@/components/seo/SEOHead";
import juniorBianchim from "@/assets/junior-bianchim.jpg";
import postCover from "@/assets/cover-treinamento-90-dias.jpg";
import postEquipe from "@/assets/mentoria-3.jpg";
import postDashboardAsset from "@/assets/plataforma-treinamento-nota-100.png.asset.json";
import postRoleplay from "@/assets/mentoria-6.jpg";
const postDashboard = postDashboardAsset.url;
import postCoverFunil from "@/assets/cover-funil-vendas.jpg";
import postCoverEstrategias from "@/assets/cover-13-estrategias.jpg";
import postCoverIA from "@/assets/cover-ia-treinamento.jpg";
import {
  ArrowRight,
  Clock,
  User,
  BookOpen,
  CheckCircle,
  ChevronRight,
  Target,
  Zap,
  Users,
  BarChart3,
  AlertTriangle,
  Lightbulb,
  Calendar,
  TrendingUp,
  Brain,
  Mic,
  Trophy,
  Timer,
  Gauge,
  XCircle,
} from "lucide-react";
import { useNavigate, Link } from "react-router-dom";

const TreinamentoVendasIA90Dias = () => {
  const navigate = useNavigate();

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline:
      "Treinamento de Vendas com IA: Como Acelerar Resultados da Equipe Comercial em 90 Dias",
    description:
      "Guia prático de treinamento de vendas com inteligência artificial: por que o modelo tradicional falha, pilares de um programa moderno, métricas, cronograma de 90 dias e erros a evitar.",
    image: postCover,
    datePublished: "2025-07-22",
    dateModified: "2025-07-22",
    author: { "@type": "Person", name: "Junior Bianchim" },
    publisher: {
      "@type": "Organization",
      name: "Bianchim Business",
      logo: { "@type": "ImageObject", url: "https://bianchimbusiness.com.br/favicon.png" },
    },
  };

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "Quanto tempo leva para ver resultado de um treinamento de vendas com IA?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Times que combinam role-play diário com IA + feedback estruturado mostram ganho de 20% a 35% em taxa de conversão entre 60 e 90 dias, segundo dados de operações B2B que acompanhei em 2024 e 2025.",
        },
      },
      {
        "@type": "Question",
        name: "Treinamento de vendas com IA substitui o gestor comercial?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Não. A IA acelera a parte repetitiva (role-play, correção de objeções, scoring de calls) e libera o gestor para coaching humano de alto impacto, decisão estratégica e desenvolvimento de carreira.",
        },
      },
      {
        "@type": "Question",
        name: "Quanto custa implantar treinamento de vendas com IA?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Plataformas como Vendas Trainer começam grátis para times pequenos. O investimento real está em tempo de gestão: cerca de 4 horas por semana nos primeiros 30 dias para configurar cenários, calibrar feedback e criar ritual de prática.",
        },
      },
    ],
  };

  const relatedPosts = [
    {
      title: "Funil de Vendas em 2026: Como Construir Pipeline Previsível Que Triplica Conversões",
      readTime: "18 min",
      link: "/posts/funil-vendas-2026",
      image: postCoverFunil,
    },
    {
      title: "13 Estratégias de Vendas Para Dominar Qualquer Negociação em 2026",
      readTime: "25 min",
      link: "/posts/13-estrategias-vendas-2026",
      image: postCoverEstrategias,
    },
    {
      title: "Como a IA Está Revolucionando o Treinamento de Equipes Comerciais",
      readTime: "6 min",
      link: "/posts/ia-treinamento-vendas",
      image: postCoverIA,
    },
  ];

  return (
    <>
      <SEOHead
        title="Treinamento de Vendas com IA: 90 Dias Para Acelerar a Equipe Comercial"
        description="Descubra como estruturar um treinamento de vendas com IA que aumenta conversão em 90 dias. Pilares, métricas, cronograma e erros a evitar — método aplicado em times B2B reais."
        keywords="treinamento de vendas, treinamento de vendas com IA, vendas, capacitação comercial, role-play vendas, sales enablement, coaching de vendas, treinamento equipe comercial"
        canonicalUrl="https://bianchimbusiness.com.br/posts/treinamento-de-vendas-com-ia-como-acelerar-resultados-da-equipe-comercial-em-90-dias"
        ogType="article"
        articlePublishedTime="2025-07-22"
        articleAuthor="Junior Bianchim"
      />

      <div className="min-h-screen bg-background">
        <Header />

        <main>
          <article>
            {/* Hero */}
            <section className="relative">
              <div className="w-full h-[50vh] sm:h-[60vh] relative overflow-hidden">
                <img
                  src={postCover}
                  alt="Junior Bianchim conduzindo mentoria presencial de treinamento de vendas com equipe comercial"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
              </div>
              <div className="container mx-auto px-4 relative -mt-32 sm:-mt-40 z-10">
                <div className="max-w-4xl mx-auto">
                  <span className="inline-block px-4 py-1.5 rounded-full bg-primary/20 text-primary text-sm font-semibold mb-4">
                    Treinamento de Vendas
                  </span>
                  <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight mb-4 text-foreground">
                    Treinamento de Vendas com IA:{" "}
                    <span className="text-gradient-brand">
                      Como Acelerar Resultados da Equipe Comercial em 90 Dias
                    </span>
                  </h1>
                  <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mt-6">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      <span>Junior Bianchim</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      <span>15 min de leitura</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <BookOpen className="w-4 h-4" />
                      <span>22 Jul 2025</span>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 1 — Introduction (1 col) */}
            <section className="py-10 sm:py-16">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <div className="prose prose-lg max-w-none text-muted-foreground space-y-6">
                    <p className="text-xl leading-relaxed">
                      <strong className="text-foreground">Você contrata um vendedor em janeiro e em julho ele ainda não bateu meta?</strong> Pior: você sabe exatamente o que ele precisa praticar, mas não tem tempo para sentar ao lado dele a cada call. Esse é o gargalo silencioso que trava 80% das operações comerciais B2B no Brasil em 2025.
                    </p>
                    <p>
                      O treinamento de vendas que funcionava em 2018 — palestra trimestral, apostila em PDF, role-play improvisado em reunião — não acompanha mais a velocidade do mercado. Comprador mais informado, ciclo de vendas mais longo, concorrência usando IA para qualificar leads em segundos. <strong className="text-foreground">A pergunta não é mais “devo treinar meu time”. É: “quanto tempo eu ainda aguento treinar do jeito antigo?”</strong>
                    </p>
                    <p>
                      Nos últimos 18 meses, ajudei times de Citróleo Group, Latina Eletrodomésticos, Mapfre e Zentai Benefícios a substituírem o velho modelo por treinamento de vendas com IA — role-play diário, feedback instantâneo, scoring objetivo e métrica clara por vendedor. <strong className="text-foreground">Este artigo é o playbook completo: por que o modelo tradicional falha, como a IA muda o jogo, métricas que importam e o cronograma realista de 90 dias para sair do zero.</strong>
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 2 — 2 columns: image + text */}
            <section className="py-10 sm:py-16 bg-muted/30">
              <div className="container mx-auto px-4">
                <div className="max-w-6xl mx-auto">
                  <div className="grid md:grid-cols-2 gap-10 items-center">
                    <div className="rounded-2xl overflow-hidden">
                      <img
                        src={postEquipe}
                        alt="Time comercial em sessão de treinamento de vendas conduzida por Junior Bianchim"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <span className="text-xs font-bold uppercase tracking-wider text-primary mb-3 block">
                        O Diagnóstico
                      </span>
                      <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4">
                        Por Que o Treinamento de Vendas Tradicional Está Falhando em 2025
                      </h2>
                      <p className="text-muted-foreground mb-4">
                        O modelo clássico tem três defeitos estruturais que ficaram impossíveis de ignorar: <strong className="text-foreground">é episódico, é genérico e é difícil de medir</strong>. Vendedor assiste a um workshop de 8 horas em fevereiro, esquece 70% do conteúdo em 30 dias (curva de Ebbinghaus) e volta a vender exatamente como vendia antes — com o mesmo script, as mesmas objeções mal respondidas e o mesmo desconto reflexo.
                      </p>
                      <p className="text-muted-foreground">
                        Enquanto isso, o comprador B2B brasileiro chega à reunião com pesquisa pronta no ChatGPT, comparativo de 4 fornecedores e perguntas técnicas que o vendedor médio não treinou para responder. <strong className="text-foreground">A distância entre o que o time sabe e o que o mercado exige cresce todo trimestre.</strong> Sem prática deliberada e contínua, essa lacuna vira meta perdida.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 3 — CTA TEST FREE */}
            <section className="py-12 sm:py-16">
              <div className="container mx-auto px-4">
                <div className="max-w-5xl mx-auto">
                  <Card className="p-8 sm:p-12 bg-gradient-to-br from-primary/10 via-card to-accent/10 border-primary/40 text-center">
                    <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-primary/20 flex items-center justify-center mx-auto mb-4">
                      <Lightbulb className="w-7 h-7 text-primary" />
                    </div>
                    <p className="text-sm font-semibold text-primary mb-2">Pausa estratégica</p>
                    <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4">
                      Antes de continuar lendo:{" "}
                      <span className="text-gradient-brand">teste o Vendas Trainer grátis</span>
                    </h2>
                    <p className="text-muted-foreground max-w-2xl mx-auto mb-8">
                      Você está prestes a ler o método completo. O caminho mais rápido para entender é ver a IA simulando uma call com seu pior tipo de cliente, dando feedback em tempo real e gerando um plano de melhoria por vendedor. <strong className="text-foreground">Crie sua conta grátis em menos de 1 minuto, sem cartão de crédito.</strong>
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      <Button
                        size="lg"
                        onClick={() => navigate("/auth?mode=signup")}
                        className="bg-gradient-to-r from-primary to-accent hover:opacity-90"
                      >
                        Testar Grátis Agora
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                      <Button size="lg" variant="outline" onClick={() => navigate("/")}>
                        Conhecer a plataforma
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-4">
                      Sem cartão de crédito · Acesso imediato · Cancele quando quiser
                    </p>
                  </Card>
                </div>
              </div>
            </section>

            {/* Section 4 — 5 columns */}
            <section className="py-10 sm:py-16">
              <div className="container mx-auto px-4">
                <div className="max-w-6xl mx-auto">
                  <div className="mb-10">
                    <span className="text-xs font-bold uppercase tracking-wider text-primary mb-3 block">
                      A Virada
                    </span>
                    <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4">
                      Como a IA Está Transformando o Treinamento de Vendas
                    </h2>
                    <p className="text-muted-foreground">
                      Cinco mudanças concretas que separam um programa moderno de treinamento de vendas de um workshop de slides. Cada uma resolve um defeito específico do modelo antigo — e juntas elas reduzem o tempo de rampa de novos vendedores em até 50%.
                    </p>
                  </div>

                  <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
                    {[
                      { icon: Mic, title: "Role-play sob demanda", text: "Vendedor treina objeção real às 22h se quiser. Sem agenda. Sem juiz humano. Sem vergonha." },
                      { icon: Brain, title: "Feedback instantâneo", text: "IA aponta hesitação, jargão, falta de descoberta e desconto reflexo na mesma call." },
                      { icon: Gauge, title: "Scoring objetivo", text: "Nota por competência (descoberta, pitch, objeção, fechamento) — não opinião do gestor." },
                      { icon: Timer, title: "Microdoses diárias", text: "15 minutos por dia retêm mais do que 8 horas por trimestre. Hábito vence evento." },
                      { icon: Trophy, title: "Gamificação real", text: "Ranking por evolução, não por sorte de carteira. Motiva quem está começando." },
                    ].map((item, i) => {
                      const Icon = item.icon;
                      return (
                        <Card key={i} className="p-5 border-primary/20 bg-card hover:border-primary/40 transition-all">
                          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center mb-3">
                            <Icon className="w-5 h-5 text-primary" />
                          </div>
                          <h3 className="font-bold text-foreground mb-2 text-sm">{item.title}</h3>
                          <p className="text-xs text-muted-foreground leading-relaxed">{item.text}</p>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              </div>
            </section>

            {/* Section 5 — 1/3 + 2/3 */}
            <section className="py-10 sm:py-16 bg-muted/30">
              <div className="container mx-auto px-4">
                <div className="max-w-6xl mx-auto">
                  <div className="grid md:grid-cols-3 gap-10">
                    <div className="md:col-span-1">
                      <span className="text-xs font-bold uppercase tracking-wider text-primary mb-3 block">
                        Estrutura
                      </span>
                      <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4">
                        Os Pilares de um Programa Moderno de Treinamento de Vendas
                      </h2>
                      <p className="text-muted-foreground text-sm">
                        Não existe IA mágica. O que existe é método: 6 pilares que precisam coexistir para que a tecnologia gere resultado em vez de gerar relatório bonito sem impacto em receita.
                      </p>
                      <div className="mt-6 rounded-2xl overflow-hidden hidden md:block">
                        <img
                          src={postRoleplay}
                          alt="Mentoria de vendas presencial com role-play e feedback ao vivo para vendedores"
                          className="w-full h-auto object-cover"
                        />
                      </div>
                    </div>
                    <div className="md:col-span-2 space-y-4">
                      {[
                        {
                          n: "01",
                          title: "Biblioteca de cenários reais",
                          text: "Cadastre as 10 objeções mais comuns do seu mercado, os 5 perfis de comprador e os 3 produtos principais. A IA simula combinando esses elementos — não cenário genérico de internet.",
                        },
                        {
                          n: "02",
                          title: "Rubrica de avaliação clara",
                          text: "Defina exatamente o que é uma boa descoberta, uma boa proposta e um bom fechamento na sua operação. Sem rubrica, feedback de IA vira opinião disfarçada de algoritmo.",
                        },
                        {
                          n: "03",
                          title: "Ritual de prática semanal",
                          text: "Bloqueio de 30 minutos no calendário, 3x por semana, com tema definido (ex: terça é objeção de preço, quinta é call de fechamento). Sem ritual, ninguém pratica.",
                        },
                        {
                          n: "04",
                          title: "Revisão de calls reais",
                          text: "1 call gravada por vendedor por semana, transcrita e analisada pela IA. O gestor comenta os 3 pontos de maior impacto. Treino vira coaching.",
                        },
                        {
                          n: "05",
                          title: "Métricas comportamentais",
                          text: "Tempo de fala do vendedor vs. cliente, número de perguntas abertas, menções a preço antes da hora. Dados que o CRM não captura e que mudam resultado.",
                        },
                        {
                          n: "06",
                          title: "Plano individual de desenvolvimento",
                          text: "Cada vendedor sai do diagnóstico com 3 competências para melhorar nos próximos 30 dias. Genérico para todos não funciona — IA permite o oposto.",
                        },
                      ].map((p) => (
                        <Card key={p.n} className="p-5 border-border hover:border-primary/40 transition-all">
                          <div className="flex gap-4">
                            <div className="text-2xl font-extrabold text-primary flex-shrink-0">{p.n}</div>
                            <div>
                              <h3 className="font-bold text-foreground mb-1">{p.title}</h3>
                              <p className="text-sm text-muted-foreground">{p.text}</p>
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 6 — 3 columns */}
            <section className="py-10 sm:py-16">
              <div className="container mx-auto px-4">
                <div className="max-w-6xl mx-auto">
                  <div className="mb-10">
                    <span className="text-xs font-bold uppercase tracking-wider text-primary mb-3 block">
                      O Que Medir
                    </span>
                    <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4">
                      Métricas de Treinamento de Vendas Que Realmente Importam
                    </h2>
                    <p className="text-muted-foreground">
                      Esqueça “horas de treinamento por colaborador”. Indicador de RH não move receita. Acompanhe estes três blocos a cada 30 dias — se algum não melhorar, o programa precisa de ajuste.
                    </p>
                  </div>

                  <div className="grid md:grid-cols-3 gap-6">
                    <Card className="p-6 border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
                      <BarChart3 className="w-6 h-6 text-primary mb-3" />
                      <h3 className="font-bold text-foreground mb-3">Comportamento</h3>
                      <ul className="space-y-2 text-sm text-muted-foreground">
                        <li className="flex gap-2"><ChevronRight className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />Talk ratio (vendedor x cliente)</li>
                        <li className="flex gap-2"><ChevronRight className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />Nº de perguntas abertas por call</li>
                        <li className="flex gap-2"><ChevronRight className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />Tempo até primeira menção a preço</li>
                        <li className="flex gap-2"><ChevronRight className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />Aderência ao playbook por etapa</li>
                      </ul>
                    </Card>
                    <Card className="p-6 border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
                      <TrendingUp className="w-6 h-6 text-primary mb-3" />
                      <h3 className="font-bold text-foreground mb-3">Performance</h3>
                      <ul className="space-y-2 text-sm text-muted-foreground">
                        <li className="flex gap-2"><ChevronRight className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />Conversão por etapa do funil</li>
                        <li className="flex gap-2"><ChevronRight className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />Ticket médio por vendedor</li>
                        <li className="flex gap-2"><ChevronRight className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />Ciclo médio de fechamento</li>
                        <li className="flex gap-2"><ChevronRight className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />Desconto médio concedido</li>
                      </ul>
                    </Card>
                    <Card className="p-6 border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
                      <Users className="w-6 h-6 text-primary mb-3" />
                      <h3 className="font-bold text-foreground mb-3">Capacitação</h3>
                      <ul className="space-y-2 text-sm text-muted-foreground">
                        <li className="flex gap-2"><ChevronRight className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />Score médio em role-plays</li>
                        <li className="flex gap-2"><ChevronRight className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />Tempo de rampa de novos</li>
                        <li className="flex gap-2"><ChevronRight className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />Frequência de prática semanal</li>
                        <li className="flex gap-2"><ChevronRight className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />Evolução por competência</li>
                      </ul>
                    </Card>
                  </div>
                </div>
              </div>
            </section>

            {/* Image break */}
            <section className="py-6">
              <div className="container mx-auto px-4">
                <div className="max-w-5xl mx-auto rounded-2xl overflow-hidden">
                  <img
                    src={postDashboard}
                    alt="Plataforma Bianchim Business mostrando treinamento com IA, simulação de venda e avaliação com nota 100"
                    className="w-full h-auto object-cover"
                  />
                </div>
              </div>
            </section>

            {/* Section 7 — 2/3 + 1/3 — Cronograma 90 dias */}
            <section className="py-10 sm:py-16 bg-muted/30">
              <div className="container mx-auto px-4">
                <div className="max-w-6xl mx-auto">
                  <div className="grid md:grid-cols-3 gap-10">
                    <div className="md:col-span-2">
                      <span className="text-xs font-bold uppercase tracking-wider text-primary mb-3 block">
                        Plano de Execução
                      </span>
                      <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-6">
                        Cronograma de 90 Dias Para Implantar Treinamento de Vendas com IA
                      </h2>
                      <div className="space-y-6">
                        <div className="border-l-4 border-primary pl-5">
                          <div className="flex items-center gap-2 mb-2">
                            <Calendar className="w-4 h-4 text-primary" />
                            <span className="font-bold text-foreground">Dias 1–30 · Diagnóstico e fundação</span>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            Mapeie as 10 objeções recorrentes, grave 3 calls por vendedor para baseline, escolha a plataforma, crie a rubrica de avaliação e treine o gestor a dar feedback baseado no relatório da IA. Sem essa base, qualquer ferramenta vira brinquedo caro.
                          </p>
                        </div>
                        <div className="border-l-4 border-primary pl-5">
                          <div className="flex items-center gap-2 mb-2">
                            <Calendar className="w-4 h-4 text-primary" />
                            <span className="font-bold text-foreground">Dias 31–60 · Ritual e prática deliberada</span>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            Ative o calendário: 3 sessões semanais de role-play de 20 minutos por vendedor, 1 call real revisada por semana, ranking de evolução semanal. Reunião quinzenal de 1h só para comentar gravações. Aqui o time começa a sentir que treino virou trabalho — não bônus.
                          </p>
                        </div>
                        <div className="border-l-4 border-primary pl-5">
                          <div className="flex items-center gap-2 mb-2">
                            <Calendar className="w-4 h-4 text-primary" />
                            <span className="font-bold text-foreground">Dias 61–90 · Métrica e ajuste fino</span>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            Cruze evolução em role-play com conversão real do CRM. Identifique os 2 vendedores que mais evoluíram e replique o padrão. Refaça a rubrica com aprendizados. Defina o próximo trimestre por competência, não por “mais um treinamento”.
                          </p>
                        </div>
                      </div>
                    </div>
                    <aside>
                      <Card className="p-6 border-primary/30 bg-gradient-to-br from-primary/10 to-transparent sticky top-24">
                        <Lightbulb className="w-6 h-6 text-primary mb-3" />
                        <h3 className="font-bold text-foreground mb-3">Regra dos 90 dias</h3>
                        <p className="text-sm text-muted-foreground mb-4">
                          Se em 90 dias a conversão por etapa do funil não mexer pelo menos um ponto percentual, o problema não é a IA — é falta de ritual ou de cenário aderente à sua realidade.
                        </p>
                        <div className="p-4 rounded-xl bg-primary/10 border border-primary/20">
                          <p className="text-xs font-bold text-primary uppercase tracking-wider mb-1">Investimento real</p>
                          <p className="text-sm text-foreground">
                            4h/semana do gestor nos primeiros 30 dias. 1h/semana do dia 31 em diante. Plataforma grátis para começar.
                          </p>
                        </div>
                      </Card>
                    </aside>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 8 — 4 columns */}
            <section className="py-10 sm:py-16">
              <div className="container mx-auto px-4">
                <div className="max-w-6xl mx-auto">
                  <div className="mb-10">
                    <span className="text-xs font-bold uppercase tracking-wider text-destructive mb-3 block">
                      Atenção
                    </span>
                    <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4">
                      Quatro Erros Que Matam Qualquer Treinamento de Vendas com IA
                    </h2>
                    <p className="text-muted-foreground">
                      Já vi operação contratar ferramenta cara, montar comitê, fazer kick-off com champanhe — e abandonar em 60 dias. Quase sempre por um destes quatro motivos.
                    </p>
                  </div>

                  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {[
                      { title: "Sem dono do programa", text: "Se a responsabilidade é \"do RH com vendas\", ninguém faz. Precisa de um sponsor no comercial com nome e KPI." },
                      { title: "Cenário genérico", text: "Role-play de SaaS americano não serve para vender consórcio no interior. Calibre com calls reais da sua operação." },
                      { title: "Sem coaching humano", text: "IA aponta o que melhorar. Quem ensina a melhorar é o gestor. Sem o segundo passo, vira relatório bonito sem evolução." },
                      { title: "Métrica errada", text: "Medir \"horas treinadas\" recompensa quem clica no play e sai. Meça evolução de score e impacto no funil." },
                    ].map((e, i) => (
                      <Card key={i} className="p-5 border-destructive/20 bg-destructive/5">
                        <XCircle className="w-5 h-5 text-destructive mb-3" />
                        <h3 className="font-bold text-foreground mb-2 text-sm">{e.title}</h3>
                        <p className="text-xs text-muted-foreground leading-relaxed">{e.text}</p>
                      </Card>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {/* Section 9 — 1 column conclusion */}
            <section className="py-10 sm:py-16">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <h2 className="text-2xl sm:text-3xl font-bold mb-6 text-foreground">
                    Treinamento de Vendas Em 2025 É Sistema, Não Evento
                  </h2>
                  <div className="prose prose-lg max-w-none text-muted-foreground space-y-4">
                    <p>
                      A diferença entre o time que cresce e o time que estagnou em 2025 não está no produto, no preço ou no CRM. Está na frequência com que o vendedor pratica deliberadamente as cinco a sete competências que decidem a venda — descoberta, qualificação, demonstração, objeção, negociação, fechamento e follow-up. Quem pratica três vezes por semana sob feedback objetivo evolui. Quem espera o workshop trimestral vai continuar perdendo para concorrente mais bem treinado.
                    </p>
                    <p>
                      A inteligência artificial não treinou ninguém sozinha — nem deve. Ela faz o que humano faz mal: estar disponível 24/7, dar feedback consistente e medir sem viés. <strong className="text-foreground">O gestor faz o que IA não faz: cria contexto, dá significado, conecta o feedback à carreira e à vida do vendedor.</strong> Programa que entende essa divisão de trabalho ganha. Programa que pede para IA substituir gestão perde.
                    </p>
                    <p>
                      Comece pelo diagnóstico esta semana. Grave três calls por vendedor. Identifique a competência mais fraca do time. Configure cinco cenários na plataforma. Bloqueie o calendário. Em 90 dias, você não vai reconhecer a operação. <strong className="text-foreground">O treinamento de vendas com IA não é o futuro — é o presente que separa quem bate meta de quem ainda promete.</strong>
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Related Posts */}
            <section className="py-10 sm:py-16 bg-muted/30">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <h2 className="text-2xl font-bold mb-8 text-foreground">Leitura Recomendada</h2>
                  <div className="grid sm:grid-cols-3 gap-6">
                    {relatedPosts.map((post, index) => (
                      <Link key={index} to={post.link} className="block group">
                        <Card className="overflow-hidden border-border hover:border-primary/30 transition-all h-full">
                          <div className="w-full h-36 overflow-hidden">
                            <img
                              src={post.image}
                              alt={`Capa do artigo ${post.title} - conteúdo complementar sobre vendas e treinamento de vendas`}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                          </div>
                          <div className="p-4">
                            <h3 className="font-semibold text-sm text-foreground group-hover:text-primary transition-colors line-clamp-2">
                              {post.title}
                            </h3>
                            <span className="text-xs text-muted-foreground mt-2 block">{post.readTime} de leitura</span>
                          </div>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {/* Author */}
            <section className="py-10 sm:py-16">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <Card className="p-6 sm:p-8 border-primary/20 bg-gradient-to-br from-card to-primary/5">
                    <div className="flex flex-col sm:flex-row items-center gap-6">
                      <div className="w-24 h-24 rounded-full overflow-hidden flex-shrink-0 ring-2 ring-primary/30">
                        <img
                          src={juniorBianchim}
                          alt="Junior Bianchim, especialista em treinamento de vendas com IA e estruturação de equipes comerciais"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="text-center sm:text-left">
                        <h3 className="text-xl font-bold text-foreground mb-1">Junior Bianchim</h3>
                        <p className="text-primary font-medium text-sm mb-3">
                          Especialista em Treinamento de Equipes Comerciais
                        </p>
                        <p className="text-muted-foreground text-sm">
                          Com mais de 20 anos de experiência em vendas B2B e B2C, Junior já estruturou operações comerciais em empresas como Citróleo Group, Latina Eletrodomésticos, Mapfre Seguros e Zentai Benefícios. Fundador do Vendas Trainer, plataforma de IA para treinamento de SDR, BDR e closers.
                        </p>
                      </div>
                    </div>
                  </Card>
                </div>
              </div>
            </section>

            {/* CTA */}
            <section className="py-16 sm:py-20 bg-muted/30">
              <div className="container mx-auto px-4">
                <Card className="p-8 sm:p-12 bg-gradient-to-br from-card via-primary/5 to-accent/10 border-primary/30 text-center max-w-4xl mx-auto">
                  <div className="relative z-10">
                    <h2 className="text-3xl md:text-4xl font-bold mb-4">
                      <span className="text-gradient-brand">Comece o Treinamento de Vendas com IA Hoje</span>
                    </h2>
                    <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
                      No Vendas Trainer, sua equipe pratica role-play diário com IA, recebe feedback instantâneo e evolui em métricas que importam — descoberta, objeção, negociação e fechamento.
                      <strong className="text-foreground"> Comece grátis hoje mesmo.</strong>
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                      <Button
                        size="lg"
                        onClick={() => navigate("/auth?mode=signup")}
                        className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-glow text-lg px-8 py-6 font-semibold"
                      >
                        Criar Conta Grátis
                        <ArrowRight className="ml-2 w-5 h-5" />
                      </Button>
                      <Button
                        size="lg"
                        variant="outline"
                        className="text-lg px-8 py-6 border-primary/50 hover:bg-primary/10"
                        asChild
                      >
                        <a href="https://wa.me/5516981960018" target="_blank" rel="noopener noreferrer">
                          Agendar Demonstração
                        </a>
                      </Button>
                    </div>
                  </div>
                </Card>
              </div>
            </section>
          </article>

          <PageNavigation />
        </main>

        <Footer />
      </div>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />
    </>
  );
};

export default TreinamentoVendasIA90Dias;
